import React, { useMemo, useState } from "react";
import { Box, Flex, HStack, Icon, IconButton, Spinner, Text, Tooltip, VStack } from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import { KGDataFrameProto } from "build-ts/dataframe";
import ArtifactPanelHeader from "modules/artifacts/ArtifactPanelHeader";
import PandasTable from "./PandasTable";
import KGDataFrameFilters from "./KGDataFrameFilters";
import { DownloadMenu } from "../../../common/DownloadMenu";
import {
  copyRawTableToClipboard,
  downloadRawTableAsExcel,
  extractRawDataFromPandasDf,
  getPandasDfRowColCount,
} from "../utils/pandasTableUtils";
import { buildKGColumnTooltips } from "./KGColumnTooltips";

export interface DataFrameProps {
  name?: string;
  /** Original data — used for expression filter source list. */
  data: KGDataFrameProto;
  /** Current displayed data — may differ from `data` after a recompute. */
  displayedData: KGDataFrameProto;
  checkedExpressionFilters: Record<string, boolean>;
  isRecomputing: boolean;
  hasExpressionFiltersChanged: boolean;
  onExpressionFilterToggle: (filter: string) => void;
  onRecompute: () => void;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
}

/**
 * Pure UI component for KGDataFrame artifacts.
 * Single-view layout: flat filter pills → expression filters → table.
 * Column headers show MKG entity metadata (name, type, time bucket, benchmark) on hover.
 */
const DataFrame: React.FC<DataFrameProps> = ({
  name,
  displayedData,
  checkedExpressionFilters,
  isRecomputing,
  hasExpressionFiltersChanged,
  onExpressionFilterToggle,
  onRecompute,
  isExpanded,
  onExpandToggle,
}) => {
  const [showHiddenColumns, setShowHiddenColumns] = useState(false);

  const { columns: numberOfColumns, rows: numberOfRows } = useMemo(
    () => getPandasDfRowColCount(displayedData.pandas_dataframe ?? undefined),
    [displayedData.pandas_dataframe]
  );

  const hasHiddenColumns = useMemo(
    () => (displayedData.ordered_columns ?? []).some(col => col.is_visible === false),
    [displayedData]
  );

  const aggregateColumns = useMemo(() => {
    return (displayedData.ordered_columns ?? [])
      .filter(col => col.is_aggregate_expression)
      .map(col => {
        const colName = col.output_column_name || "";
        const evalExpression = col.eval_expression || "";
        let value: any = null;
        if (col.data_value_json_array) {
          try {
            const sanitized = col.data_value_json_array
              .replace(/\bNaN\b/g, "null")
              .replace(/\bInfinity\b/g, "null")
              .replace(/\b-Infinity\b/g, "null");
            const parsed = JSON.parse(sanitized);
            value = Array.isArray(parsed) ? parsed[0] : parsed;
          } catch (error) {
            console.error(`Failed to parse aggregate data for column ${colName}:`, error);
          }
        }
        return {
          name: colName,
          eval_expression: evalExpression,
          value: value !== null && value !== undefined ? String(value) : "N/A",
        };
      });
  }, [displayedData]);

  const columnTooltips = useMemo(() => buildKGColumnTooltips(displayedData), [displayedData]);

  const headerActions = (
    <>
      {numberOfRows > 0 && (
        <Text fontSize="xs" color="gray.700" mx={2} mb={0}>
          {numberOfColumns} {numberOfColumns > 1 ? "cols" : "col"}, {numberOfRows} {numberOfRows > 1 ? "rows" : "row"}
        </Text>
      )}

      {hasHiddenColumns && (
        <Tooltip
          label={showHiddenColumns ? "Hide Intermediate Columns" : "Show Intermediate Columns"}
          placement="bottom"
          hasArrow
          fontSize="xs"
        >
          <IconButton
            icon={<Icon as={showHiddenColumns ? AppIcons.eye : AppIcons.eyeOff} boxSize={4} />}
            aria-label={showHiddenColumns ? "Hide Intermediate Columns" : "Show Intermediate Columns"}
            size="xs"
            color={showHiddenColumns ? "purple.600" : "gray.700"}
            bg={showHiddenColumns ? "purple.50" : "transparent"}
            _hover={{ bg: "purple.50", color: "purple.600" }}
            onClick={() => setShowHiddenColumns(prev => !prev)}
          />
        </Tooltip>
      )}

      {displayedData.pandas_dataframe && (
        <DownloadMenu
          ariaLabel="Download"
          items={[
            {
              label: "Copy to clipboard",
              icon: "copy",
              onClick: () => copyRawTableToClipboard(extractRawDataFromPandasDf(displayedData.pandas_dataframe)),
            },
            {
              label: "Download Excel file",
              icon: "fileExcel",
              onClick: () =>
                downloadRawTableAsExcel(
                  extractRawDataFromPandasDf(displayedData.pandas_dataframe),
                  `${name ?? "dataframe"}.xlsx`
                ),
            },
          ]}
        />
      )}

      {displayedData.error_messages && displayedData.error_messages.length > 0 && (
        <Tooltip
          label={
            <Box as="ul" pl={4} maxW="300px" fontSize="xs">
              {displayedData.error_messages.map((error, index) => (
                <Box as="li" key={index}>
                  {error}
                </Box>
              ))}
            </Box>
          }
          placement="bottom"
          hasArrow
          bg="red.600"
        >
          <IconButton
            icon={<Icon as={AppIcons.warning} boxSize={4} />}
            aria-label="Show errors"
            size="xs"
            color="red.500"
            bg="transparent"
            _hover={{ bg: "red.50", color: "red.600" }}
            onClick={e => e.stopPropagation()}
          />
        </Tooltip>
      )}
    </>
  );

  return (
    <Box position="relative" display="flex" flexDirection="column" width="100%" flex="1 1 0" minH={0} overflow="hidden">
      <ArtifactPanelHeader
        icon={AppIcons.database}
        iconBg="white"
        iconColor="purple.700"
        name={name ?? ""}
        typeLabel="Data Table"
        actions={headerActions}
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
      />

      <Box
        flex="1 1 0"
        minH={0}
        display="flex"
        flexDirection="column"
        overflowY="auto"
        overflowX="hidden"
        onWheel={e => e.stopPropagation()}
        sx={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "var(--chakra-colors-gray-300)", borderRadius: "2px" },
        }}
      >
        <VStack align="stretch" spacing={0} textAlign="left" px={0} overflowY="hidden">
          {/* ── Filter pills (always visible, no toggle) ── */}
          <KGDataFrameFilters
            data={displayedData}
            checkedExpressionFilters={checkedExpressionFilters}
            isRecomputing={isRecomputing}
            hasExpressionFiltersChanged={hasExpressionFiltersChanged}
            onExpressionFilterToggle={onExpressionFilterToggle}
            onRecompute={onRecompute}
          />

          {/* ── Table ── */}
          {isRecomputing ? (
            <Flex justify="center" align="center" h="120px">
              <Spinner size="md" color="purple.600" thickness="2px" />
            </Flex>
          ) : (
            <PandasTable df={displayedData.pandas_dataframe} columnTooltips={columnTooltips} />
          )}

          {/* ── Aggregate values ── */}
          {aggregateColumns.length > 0 && (
            <Box p={2} bg="purple.50" borderTopWidth="1px" borderColor="gray.200">
              <HStack spacing={2} flexWrap="wrap">
                {aggregateColumns.map((aggCol, index) => (
                  <Box key={index}>
                    <Text fontSize="sm" fontWeight="medium">
                      <Tooltip
                        label={aggCol.eval_expression || "No expression available"}
                        placement="top"
                        hasArrow
                        bg="gray.700"
                      >
                        <Text as="span" color="gray.700" cursor="pointer">
                          {aggCol.name}
                        </Text>
                      </Tooltip>
                      <Text as="span" color="gray.700" mx={1}>
                        =
                      </Text>
                      <Text as="span" color="purple.600" fontWeight="semibold">
                        {aggCol.value}
                      </Text>
                    </Text>
                  </Box>
                ))}
              </HStack>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default DataFrame;
